/**
 * The site footer, in one place.
 *
 * It was previously rebuilt by hand in Home, Contact, Projects and
 * ProjectDetail — four copies of the same link list, in three different styles
 * (template literal, joined string array, DOM construction). Adding a single
 * link meant four edits in three idioms, and the copies had already drifted:
 * only Home carried the social row.
 *
 * `renderFooter()` returns markup, for pages assembling a big HTML string.
 * `footerElement()` returns a node, for pages building the DOM directly.
 *
 * Social links stay opt-in so this refactor changes structure and not output;
 * only Home passes `social: true`, exactly as before. Turn them on everywhere
 * once the placeholder URLs below point at real profiles.
 */

export const footerLinks = [
  ['Work', '/projects'],
  ['About', '/about'],
  ['Blog', '/blog'],
  ['Guides', '/guides'],
  ['Contact', '/contact'],
];

// TODO: these point at the platforms, not at any profile.
export const socialLinks = [
  ['Twitter', 'https://twitter.com'],
  ['LinkedIn', 'https://linkedin.com'],
  ['GitHub', 'https://github.com'],
];

const COPYRIGHT = `© ${new Date().getFullYear()} Caden Burleson. All rights reserved.`;
const WORDMARK = 'Caden Burleson';

/** Everything inside <footer>, shared by both entry points below. */
function footerInnerHTML({ social = false } = {}) {
  const links = footerLinks.map(([label, href]) => `<a href="${href}">${label}</a>`).join('\n            ');

  const socialRow = social
    ? `
          <div class="footer-social">
            ${socialLinks
              .map(([label, href]) => `<a href="${href}" target="_blank" rel="noopener">${label}</a>`)
              .join('\n            ')}
          </div>`
    : '';

  return `
      <div class="rm-container">
        <div class="footer-content">
          <div class="footer-links">
            ${links}
          </div>${socialRow}
          <div class="footer-copy">
            <p>${COPYRIGHT}</p>
          </div>
        </div>
      </div>
      <div class="footer-name">${WORDMARK}</div>`;
}

/** Full <footer> markup, for pages built as an HTML string. */
export function renderFooter(options = {}) {
  return `<footer class="rm-footer">${footerInnerHTML(options)}</footer>`;
}

/** A detached <footer> node, for pages built with DOM calls. */
export function footerElement(options = {}) {
  const footer = document.createElement('footer');
  footer.className = 'rm-footer';
  footer.innerHTML = footerInnerHTML(options);
  return footer;
}
